import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Button, Input } from 'semantic-ui-react';
import Proptype from 'prop-types';
import Message from 'components/common/Message';
import { organizationsDropDownOptions } from 'utils/dropdownOptions';
import listOrganizations from 'redux/actions/organizations/listOrganizations';
import listProjects from 'redux/actions/projects/listProjects';
import getMicroloansSettingData from 'redux/actions/settings/getMicroloansSettingData';
import updateMicroloansSettings from 'redux/actions/settings/updateMicroloansSettings';
import ConfirmPinModal from 'components/common/modals/ConfirmPinModal';
import labelFormatter from 'helpers/labelFormatter';

const interestRateOptions = [
  { key: 'default', text: 'Default', value: 'default' },
  { key: 'custom', text: 'Custom', value: 'custom' },
];

const OrgInterestDetails = ({ type }) => {
  const [form, setForm] = useState({
    organization_id: null,
    interest_rate_type: 'default',
    overall_rate: '',
    project_rates: {},
  });
  const [openPinModal, setOpenPinModal] = useState(false);
  const [userPin, setUserPin] = useState('');

  const dispatch = useDispatch();

  const organizations = useSelector(
    ({ organizations: { list } }) => list?.items,
  );

  const projects = useSelector(
    ({ projects: { list } }) => list?.items,
  );

  const { loading, error, data } = useSelector(
    ({ settings: { microloanSettingsData } }) =>
      microloanSettingsData,
  );

  const { loading: updateLoading, error: updateError } = useSelector(
    ({ settings: { updateMicroloanSettings } }) =>
      updateMicroloanSettings,
  );

  useEffect(() => {
    listOrganizations()(dispatch);
  }, []);

  // Populate form from API data and initialise per-project rate entries
  useEffect(() => {
    if (!data && !projects) return;
    setForm(prev => {
      // Merge API response data when available
      const apiRates =
        data?.projects?.reduce((acc, project) => {
          acc[project.project_id] = {
            interest: project.interest,
          };
          return acc;
        }, {}) ?? {};

      // Build project rates: use API values first, then existing form values, then defaults
      const projectRates = projects
        ? projects.reduce((acc, project) => {
            acc[project.id] = apiRates[project.id]
              ? {
                  name: project.name,
                  ...apiRates[project.id],
                }
              : prev.project_rates[project.id] || {
                  name: project.name,
                  interest: '',
                };
            return acc;
          }, {})
        : apiRates;

      return {
        ...prev,
        interest_rate_type:
          data?.interest_rate_type || prev.interest_rate_type,
        overall_rate: data?.overall_rate ?? prev.overall_rate,
        project_rates: projectRates,
      };
    });
  }, [data, projects]);

  const handleOrgChange = (_, { value }) => {
    setForm({
      organization_id: value,
      interest_rate_type: 'default',
      overall_rate: '',
      project_rates: {},
    });
    listProjects(value)(dispatch);
    getMicroloansSettingData({
      section: 'OrgInterest',
      type,
      organization_id: value,
    })(dispatch);
  };

  const handleRateTypeChange = (_, { value }) => {
    setForm(prev => ({ ...prev, interest_rate_type: value }));
  };

  const handleOverallRateChange = (_, { value }) => {
    setForm(prev => ({ ...prev, overall_rate: value }));
  };

  const handleProjectRateChange = (projectId, value) => {
    setForm(prev => ({
      ...prev,
      project_rates: {
        ...prev.project_rates,
        [projectId]: {
          ...prev.project_rates[projectId],
          interest: value,
        },
      },
    }));
  };

  const handleSubmit = () => {
    const projectRatesPayload = Object.entries(
      form.project_rates,
    ).map(([id, { interest }]) => ({ project_id: id, interest }));

    const isCustomType = form.interest_rate_type === 'custom';
    const hasCustomOverallRate = isCustomType && form.overall_rate;

    const payload = {
      organization_id: form.organization_id,
      interest_rate_type: form.interest_rate_type,
    };

    if (hasCustomOverallRate) {
      payload.overall_rate = form.overall_rate;
    } else if (isCustomType) {
      payload.overall_rate = '';
      payload.projects = projectRatesPayload;
    }

    updateMicroloansSettings({
      password: userPin,
      section: 'OrgInterest',
      type,
      data: payload,
    })(dispatch, () => setOpenPinModal(false));
  };

  const isCustom = form.interest_rate_type === 'custom';
  const hasOverallRate =
    isCustom && String(form.overall_rate).trim() !== '';

  return (
    <>
      <ConfirmPinModal
        content={`Are you sure you want to update the Org Interest settings for ${labelFormatter(
          type,
        )}`}
        openModal={openPinModal}
        setOpenModal={setOpenPinModal}
        userPin={userPin}
        setUserPin={setUserPin}
        error={{ message: updateError }}
        loading={updateLoading}
        hasComplexSubmitAction
        handleSubmit={handleSubmit}
      />
      <Form
        onSubmit={() => setOpenPinModal(!openPinModal)}
        loading={loading}
      >
        {!loading && error && (
          <Message message={error.message} color="red" />
        )}

        <Form.Group widths="equal">
          <Form.Dropdown
            fluid
            selection
            label="Choose Organization"
            placeholder="Select an organization"
            options={
              organizationsDropDownOptions(organizations) || []
            }
            value={form.organization_id}
            onChange={handleOrgChange}
          />

          {form.organization_id && (
            <Form.Dropdown
              fluid
              selection
              label="Interest rate setting"
              placeholder="-- Select --"
              options={interestRateOptions}
              value={form.interest_rate_type}
              onChange={handleRateTypeChange}
            />
          )}

          {form.organization_id && isCustom && (
            <Form.Field
              control={Input}
              type="number"
              step="0.1"
              label="Overall organization interest rate (%)"
              placeholder="Input custom rate (the default rate is 3.5%)"
              value={form.overall_rate}
              onChange={handleOverallRateChange}
            />
          )}
        </Form.Group>

        {form.organization_id && isCustom && (
          <>
            <h4>Projects</h4>
            {Object.entries(form.project_rates).map(
              ([projectId, { name, interest }]) => (
                <Form.Field
                  key={projectId}
                  control={Input}
                  type="number"
                  step="0.1"
                  label={`${name} interest rate (%)`}
                  disabled={hasOverallRate}
                  value={hasOverallRate ? '' : interest}
                  placeholder={
                    hasOverallRate
                      ? `Set to the custom overall organization rate - ${form.overall_rate}%`
                      : ''
                  }
                  onChange={(_, { value }) =>
                    handleProjectRateChange(projectId, value)
                  }
                />
              ),
            )}
          </>
        )}

        <div className="action">
          <Button
            type="submit"
            primary
            loading={updateLoading}
            disabled={updateLoading || !form.organization_id}
          >
            Save
          </Button>
        </div>
      </Form>
    </>
  );
};

OrgInterestDetails.propTypes = {
  type: Proptype.string.isRequired,
};

export default OrgInterestDetails;
